import { Translate } from 'next-translate';
import { NextRouter } from 'next/router';
import { Icon } from 'antd-v3';
import {
  BookOutlined,
  IdcardOutlined,
  LayoutFilled,
  LineChartOutlined,
  NotificationOutlined,
  PlusCircleOutlined,
  RubyOutlined,
  SettingOutlined,
  TableOutlined,
  TeamOutlined
} from '@ant-design/icons';

import { MenuItem } from '@components/layout/SideMenu/types';
import { OpenRfxDrawer } from '@components/providers/RfxDrawerProvider/RfxDrawerProvider';
import { IconWithBadge } from '@components/layout/SideMenu/IconWithBadge';
import Routes from '@services/Routes';
import { briefcase } from '@utils/icons';
import { isDev as isDevEnv } from '@utils/constants';

export const getDefaultMenuItems = (
  t: Translate,
  user: any,
  router: NextRouter,
  unreadCount = 0,
  isAutomated = false,
  enableValueOverviewDashboard = false,
  openRfxDrawer: OpenRfxDrawer,
  currentPathName: string,
  enableAdoptionReporting = false,
  enableCatalog = false,
  enablePriceUpdate = false,
  enableNewSettingsPage = false,
): MenuItem[] => {
  const isDeeplink = user.is_deeplink;
  const isBuyer = user.is_buyer && !isDeeplink;
  const isSuperAdmin = user.category === 'superadmin';
  const deeplinkContextID = user.deeplink_contextable_id;
  const showSettings = enableNewSettingsPage && !!user.id && !isDeeplink && !isSuperAdmin;

  const menuItems: MenuItem[] = [
    {
      icon: <LayoutFilled />,
      id: 'dashboard',
      path: '/bid_requests',
      text: t('side_menu.dashboard'),
      visible: !isSuperAdmin && !isDeeplink,
    },
    {
      icon: <LayoutFilled />,
      id: 'deeplink-dashboard',
      path: `/bid_requests/${deeplinkContextID}/dashboard`,
      text: t('side_menu.request_overview'),
      visible: isDeeplink,
    },
    {
      children: [
        {
          id: 'new-rfp',
          onClick: () => openRfxDrawer('rfp'),
          text: t('side_menu.new_rfp'),
          visible: true,
        },
        {
          id: 'new-rfi',
          onClick: () => openRfxDrawer('rfi'),
          text: t('side_menu.new_rfi'),
          visible: true,
        },
        {
          id: 'new-price-update',
          onClick: () => openRfxDrawer('price_update'),
          text: t('side_menu.new_price_update'),
          visible: isBuyer && enablePriceUpdate,
        },
        {
          id: 'new-rff',
          onClick: () => openRfxDrawer('rff'),
          text: t('side_menu.new_rff'),
          visible: true,
        }
      ],
      icon: <NotificationOutlined />,
      id: 'new-event-wizard',
      path: currentPathName,
      text: t('side_menu.new_bid_request'),
      visible: isBuyer
    },
    {
      icon: <IconWithBadge unreadCount={unreadCount} />,
      id: 'messages',
      path: Routes.MESSAGES_PATH,
      text: t('general.messages'),
      visible: !isSuperAdmin && !isDeeplink,
    },
    {
      icon: <IconWithBadge unreadCount={unreadCount} />,
      id: 'deeplink-messages',
      path: Routes.REQUEST_MESSAGES_PATH(deeplinkContextID),
      text: t('general.messages'),
      visible: isDeeplink && !isAutomated,
    },
    {
      icon: <Icon component={briefcase} />,
      id: 'organizations',
      path: '/platform_administration/organizations',
      text: t('side_menu.organizations'),
      visible: isSuperAdmin,
    },
    {
      icon: <TeamOutlined />,
      id: 'users',
      path: '/platform_administration/users',
      text: t('side_menu.users'),
      visible: isSuperAdmin,
    },
    {
      icon: <PlusCircleOutlined />,
      id: 'events',
      path: '/platform_administration/bid_requests',
      text: t('side_menu.bid_requests'),
      visible: isSuperAdmin,
    },
    {
      icon: <BookOutlined />,
      id: 'api-v1-docs',
      path: '/api_docs',
      text: t('side_menu.api_v1_docs'),
      visible: isSuperAdmin && isDevEnv
    },
    {
      icon: <BookOutlined />,
      id: 'api-v2-docs',
      path: '/api-docs',
      text: t('side_menu.api_v2_docs'),
      visible: isSuperAdmin && isDevEnv
    },
    {
      icon: <RubyOutlined />,
      id: 'ruby-docs',
      path: '/ruby_docs',
      text: t('side_menu.ruby_docs'),
      visible: isSuperAdmin && isDevEnv
    },
    {
      icon: <TableOutlined />,
      id: 'catalog',
      path: '/catalog',
      text: t('side_menu.catalog'),
      visible: isBuyer && enableCatalog,
    },
    {
      icon: <IdcardOutlined />,
      id: 'suppliers',
      path: '/suppliers',
      text: t('side_menu.suppliers'),
      visible: isBuyer,
    },
    {
      icon: <TeamOutlined />,
      id: 'my-team',
      path: '/team',
      text: t('side_menu.team'),
      visible: isBuyer,
    },
    // Uncomment when we have a need for internal services
    // {
    //   icon: <ToolOutlined />,
    //   path: '/platform_administration/services',
    //   text: t('side_menu.services'),
    //   visible: isSuperAdmin,
    // },
    ...(enableAdoptionReporting
      ? [{
        children: [
          ...(enableValueOverviewDashboard ? [{
            id: 'value-overview',
            onClick: () => { router.push('/reporting/value_overview'); },
            text: t('side_menu.value_overview'),
            visible: true,
          }] : []),
          {
            id: 'adoption-reporting',
            onClick: () => { router.push(Routes.ADOPTION_PATH); },
            text: t('side_menu.adoption_report'),
            visible: true,
          },
        ],
        icon: <LineChartOutlined />,
        id: 'reporting',
        path: currentPathName,
        text: t('side_menu.reporting'),
        visible: isBuyer,
      }]
      : [{
        icon: <LineChartOutlined />,
        id: 'value-overview',
        onClick: () => { router.push('/reporting/value_overview'); },
        path: '/reporting/value_overview',
        text: t('side_menu.value_overview'),
        visible: isBuyer && enableValueOverviewDashboard,
      }]
    ),
    {
      icon: <SettingOutlined />,
      id: 'settings',
      path: '/settings/profile',
      text: t('general.settings'),
      visible: showSettings,
    },
  ];

  return menuItems;
};
